import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import {
  AllCommunityModule,
  ColDef,
  ModuleRegistry,
  colorSchemeDark,
  themeQuartz,
  type CellContextMenuEvent,
  type CellKeyDownEvent,
} from 'ag-grid-community';
import { ClipboardCopy } from 'lucide-react';
import {
  LopGridContextMenu,
  type LopMenuItem,
} from '../LopGridContextMenu';
import { t } from '../../i18n/t';
import { useTheme } from '../../contexts/ThemeContext';
import './LopAgGrid.css';

// Single registration point. Swap AllCommunityModule → AllEnterpriseModule
// (and add LicenseManager.setLicenseKey(...)) when upgrading to Enterprise.
ModuleRegistry.registerModules([AllCommunityModule]);

const lopTheme = themeQuartz;

// mizrahi only: grids are normally left on plain themeQuartz (data stays
// neutral), but this light fintech theme restyles them to match the shell.
// Module-level constant -> stable identity (AgGrid resets column state when the
// `theme` prop identity changes). Row height / font size are NOT overridden.
const mizrahiGridTheme = themeQuartz.withParams({
  fontFamily: ["Heebo", "Assistant", "Segoe UI", "Arial Hebrew", "Arial", "sans-serif"],
  accentColor: 'var(--color-brand)',
  backgroundColor: '#ffffff',
  foregroundColor: '#22252b',
  headerBackgroundColor: '#fafbfd',
  headerTextColor: '#6b7080',
  headerFontWeight: 600,
  borderColor: '#eceef3',
  rowBorder: { style: 'solid', width: 1, color: '#eceef3' },
  oddRowBackgroundColor: '#fbfbfd',
  rowHoverColor: 'var(--color-brand-row-hover)',
  selectedRowBackgroundColor: 'var(--color-brand-subtle)',
  rangeSelectionBorderColor: 'var(--color-brand)',
  rangeSelectionBackgroundColor: 'color-mix(in srgb, var(--color-brand) 12%, transparent)',
  inputFocusBorder: { style: 'solid', width: 1, color: 'var(--color-brand)' },
  wrapperBorderRadius: 14,
  checkboxCheckedBackgroundColor: 'var(--color-brand)',
});

// ── Dark variants ──────────────────────────────────────────────────────────
// colorSchemeDark flips AgGrid's native bits (menus, popups, scrollbars, form
// controls via browserColorScheme); the params below pull every surface from
// the app's CSS tokens (tokens.css dark blocks) so ONE constant serves lop and
// discount, and mizrahi adds its own shape/typography. All MODULE-LEVEL for a
// stable `theme` identity (CLAUDE.md: every prop must be memoized — it only
// changes when the user flips theme/scheme, which is a deliberate re-theme).
const darkGridParams = {
  backgroundColor: 'var(--color-surface)',
  foregroundColor: 'var(--color-text-primary)',
  chromeBackgroundColor: 'var(--color-surface-raised)',
  headerBackgroundColor: 'var(--color-surface-raised)',
  headerTextColor: 'var(--color-text-secondary)',
  headerFontWeight: 600,
  borderColor: 'var(--color-border)',
  rowBorder: { style: 'solid', width: 1, color: 'var(--color-border)' },
  oddRowBackgroundColor: 'color-mix(in srgb, var(--color-text-primary) 3.5%, var(--color-surface))',
  rowHoverColor: 'color-mix(in srgb, var(--color-text-primary) 8%, var(--color-surface))',
  selectedRowBackgroundColor: 'var(--color-primary-subtle)',
  menuBackgroundColor: 'var(--color-surface-raised)',
  accentColor: 'var(--color-primary-fg)',
  rangeSelectionBorderColor: 'var(--color-primary-fg)',
  rangeSelectionBackgroundColor: 'color-mix(in srgb, var(--color-primary-fg) 16%, transparent)',
  inputFocusBorder: { style: 'solid', width: 1, color: 'var(--color-primary-fg)' },
  checkboxCheckedBackgroundColor: 'var(--color-primary)',
  checkboxUncheckedBorderColor: 'var(--color-border-strong)',
  inputBorder: { style: 'solid', width: 1, color: 'var(--color-border-strong)' },
};
const darkGridTheme = themeQuartz.withPart(colorSchemeDark).withParams(darkGridParams);
const mizrahiDarkGridTheme = themeQuartz.withPart(colorSchemeDark).withParams({
  ...darkGridParams,
  fontFamily: ["Heebo", "Assistant", "Segoe UI", "Arial Hebrew", "Arial", "sans-serif"],
  wrapperBorderRadius: 14,
});

// Hebrew for AgGrid's own texts (overlays like "No Matching Rows" + the column
// filter menu). Module-level -> stable identity. A caller's localeText wins.
const GRID_LOCALE_KEYS = [
  'noRowsToShow', 'noMatchingRows', 'loadingOoo', 'loadingError', 'filterOoo', 'searchOoo',
  'selectAll', 'blanks', 'equals', 'notEqual', 'contains', 'notContains', 'startsWith',
  'endsWith', 'blank', 'notBlank', 'lessThan', 'greaterThan', 'lessThanOrEqual',
  'greaterThanOrEqual', 'inRange', 'inRangeStart', 'inRangeEnd', 'before', 'after',
  'andCondition', 'orCondition', 'applyFilter', 'resetFilter', 'clearFilter', 'cancelFilter',
  'true', 'false',
] as const;
const lopLocaleText: Record<string, string> = Object.fromEntries(
  GRID_LOCALE_KEYS.map((k) => [k, t(`gridLocale.${k}`)]),
);

const lopDefaultColDef: ColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  flex: 1,
  minWidth: 100,
};

export type LopAgGridProps<TData = any> = AgGridReactProps<TData>;

// Copy a cell value to the clipboard (raw value — matches the LopCrudPage
// right-click "העתק", so copy behaves the same everywhere).
function copyCellValue(value: unknown): void {
  void navigator.clipboard.writeText(value == null ? '' : String(value));
}

function LopAgGridInner<TData>(
  props: LopAgGridProps<TData>,
  ref: React.ForwardedRef<AgGridReact<TData>>,
) {
  const {
    defaultColDef,
    theme,
    enableRtl,
    columnDefs,
    onCellContextMenu,
    onCellKeyDown,
    ...rest
  } = props;

  const { theme: appTheme, resolvedScheme } = useTheme();
  const isDark = resolvedScheme === 'dark';
  const gridTheme =
    theme ??
    (appTheme === 'mizrahi'
      ? isDark ? mizrahiDarkGridTheme : mizrahiGridTheme
      : isDark ? darkGridTheme : lopTheme);

  const mergedDefaultColDef = useMemo<ColDef>(
    () => ({ ...lopDefaultColDef, ...(defaultColDef ?? {}) }),
    [defaultColDef],
  );

  // Default `headerTooltip` to `headerName` so browsers render a native title
  // attribute on hover — long column headers that get truncated will reveal
  // their full text. Only fills in the tooltip if the column doesn't already
  // define one; explicit nulls/undefineds inherit from headerName.
  const enrichedColumnDefs = useMemo(() => {
    if (!columnDefs) return columnDefs;
    return columnDefs.map((c: any) =>
      c?.headerTooltip == null && typeof c?.headerName === 'string'
        ? { ...c, headerTooltip: c.headerName }
        : c,
    );
  }, [columnDefs]);

  // ── Cell copy — available on EVERY grid ─────────────────────────────────
  // Ctrl+C / Cmd+C on a focused cell copies its value. This runs on all grids
  // (including LopCrudPage's) and is skipped while a cell is being edited so
  // it doesn't hijack the editor's own copy. Chains to any consumer handler.
  const handleCellKeyDown = useCallback(
    (e: CellKeyDownEvent<TData>) => {
      const ke = e.event as KeyboardEvent | undefined;
      const editing = e.api.getEditingCells().length > 0;
      if (
        !editing &&
        ke &&
        (ke.ctrlKey || ke.metaKey) &&
        (ke.key === 'c' || ke.key === 'C')
      ) {
        copyCellValue(e.value);
      }
      onCellKeyDown?.(e);
    },
    [onCellKeyDown],
  );

  // Right-click "העתק". Only wired when the consumer did NOT supply its own
  // onCellContextMenu — LopCrudPage / useGridContextMenu pass a richer menu
  // (copy/paste/export/navigate) and take precedence. Raw grids get this
  // minimal copy menu so copy works everywhere.
  const [copyMenu, setCopyMenu] = useState<{
    x: number;
    y: number;
    value: unknown;
  } | null>(null);

  const defaultCellContextMenu = useCallback((e: CellContextMenuEvent<TData>) => {
    const me = e.event as MouseEvent | null;
    if (!me) return;
    me.preventDefault();
    setCopyMenu({ x: me.clientX, y: me.clientY, value: e.value });
  }, []);

  const copyMenuItems = useMemo<LopMenuItem[]>(
    () =>
      copyMenu
        ? [
            {
              label: t('contextMenu.copy'),
              icon: <ClipboardCopy size={16} />,
              onClick: () => copyCellValue(copyMenu.value),
            },
          ]
        : [],
    [copyMenu],
  );

  return (
    <>
      <AgGridReact<TData>
        ref={ref}
        theme={gridTheme}
        enableRtl={enableRtl ?? true}
        defaultColDef={mergedDefaultColDef}
        columnDefs={enrichedColumnDefs}
        onCellKeyDown={handleCellKeyDown}
        onCellContextMenu={onCellContextMenu ?? defaultCellContextMenu}
        // Use native browser tooltips (title="...") instead of AgGrid's custom
        // tooltip component. We default `headerTooltip = headerName` above so
        // long column titles reveal their full text on hover — but AgGrid v35's
        // custom tooltip system requires registering a tooltip component AND
        // doesn't manifest as a `title` attribute. enableBrowserTooltips=true
        // is lighter (zero JS), works immediately, and is the right call for
        // simple "show full text on hover" UX.
        enableBrowserTooltips
        localeText={lopLocaleText}
        {...rest}
      />
      {copyMenu && (
        <LopGridContextMenu
          x={copyMenu.x}
          y={copyMenu.y}
          items={copyMenuItems}
          onClose={() => setCopyMenu(null)}
        />
      )}
    </>
  );
}

// forwardRef + generics: cast preserves the <TData> parameter at call sites.
export const LopAgGrid = forwardRef(LopAgGridInner) as <TData = any>(
  props: LopAgGridProps<TData> & { ref?: React.ForwardedRef<AgGridReact<TData>> },
) => ReturnType<typeof LopAgGridInner>;
